import React, { useEffect, useState } from "react";
import { PageHero, Section, Reveal, Eyebrow, GoldRule } from "@/components/Editorial";
import { fetchSettings } from "@/lib/api";
import {
  TextField,
  SelectField,
  ConsentField,
  SubmitButton,
  SuccessNote,
  useEnquirySubmit,
} from "@/components/EnquiryFields";

const DESKS = [
  { title: "Trade Enquiries", body: "For hospitality, retail, diplomatic and institutional buyers seeking portfolio access or supply arrangements." },
  { title: "Brand Introductions", body: "For breweries, wineries and spirit houses considering representation in the Indian market." },
  { title: "Distribution Partnerships", body: "For established distributors interested in carrying the HCC portfolio in their region." },
];

const CATEGORIES = [
  { value: "", label: "Select an enquiry category", disabled: true },
  { value: "business", label: "Trade / Business Enquiry" },
  { value: "brand-principal", label: "Brand Introduction (Producer / Principal)" },
  { value: "hospitality-retail", label: "Hospitality & Retail Buying" },
  { value: "logistics-supply", label: "Logistics & Supply" },
  { value: "general", label: "General / Other" },
];

const EMPTY = { type: "", name: "", company: "", email: "", phone: "", message: "" };

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const { status, error, submit } = useEnquirySubmit();

  if (status === "success") return <SuccessNote testId="contact-success" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      data-testid="contact-form"
      className="max-w-2xl space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        submit({ ...form, consent: consent ? "true" : "false" });
      }}
    >
      <SelectField
        label="Enquiry category"
        required
        testId="contact-type-select"
        options={CATEGORIES}
        value={form.type}
        onChange={set("type")}
      />
      <div className="grid sm:grid-cols-2 gap-6">
        <TextField label="Full name" required testId="contact-name-input" value={form.name} onChange={set("name")} />
        <TextField label="Company / Organisation" testId="contact-company-input" value={form.company} onChange={set("company")} />
        <TextField label="Email" type="email" required testId="contact-email-input" value={form.email} onChange={set("email")} />
        <TextField label="Phone" testId="contact-phone-input" value={form.phone} onChange={set("phone")} />
      </div>
      <TextField label="Message" required textarea testId="contact-message-input" value={form.message} onChange={set("message")} />
      <ConsentField checked={consent} onChange={setConsent} testId="contact-consent-checkbox" />
      {status === "error" && (
        <p data-testid="contact-form-error" className="text-[13px] text-[#4B0F1E]">
          {error}
        </p>
      )}
      <SubmitButton sending={status === "sending"} testId="contact-submit-btn">
        Submit Enquiry
      </SubmitButton>
    </form>
  );
};

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => {});
  }, []);

  return (
    <div data-testid="contact-page" className="bg-[#F7F1E7]">
      <PageHero
        eyebrow="Contact"
        title="Speak with our team."
        lede="Our trade desk responds to enquiries from trade, institutional and brand partners. Communication is handled with the discretion the industry expects."
      />

      <Section tint>
        <div className="grid sm:grid-cols-3 gap-x-12 gap-y-10 max-w-5xl">
          {DESKS.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.08}>
              <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
              <h3 className="hcc-serif text-xl mt-3 text-[#1c1713]">{d.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/65">{d.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid lg:grid-cols-[1fr_320px] gap-x-20 gap-y-14 max-w-6xl">
          <Reveal>
            <Eyebrow>Enquiry Form</Eyebrow>
            <h2 className="hcc-serif text-3xl font-medium text-[#1c1713] mt-4">Send us an enquiry.</h2>
            <p className="mt-4 mb-10 text-sm font-light leading-relaxed text-[#1c1713]/65 max-w-xl">
              Select the desk your enquiry concerns and our team will route it accordingly.
            </p>
            <ContactForm />
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-2 text-sm font-light text-[#1c1713]/70" data-testid="contact-office">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4B0F1E]">Registered Office</p>
              <p className="pt-2">{settings?.company_name || "Hema Connoisseur Collections (P) Limited"}</p>
              <p>{settings?.address_line1 || "C-58/3 Okhla Industrial Area Phase-2"}</p>
              <p>{settings?.address_line2 || "New Delhi 110020, India"}</p>
            </div>
            <GoldRule className="mt-7" />
            <div className="mt-7 space-y-2 text-sm font-light text-[#1c1713]/70" data-testid="contact-details">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4B0F1E]">Direct</p>
              <p className="pt-2">
                <a href={`tel:${(settings?.phone || "+91.11.41032500").replace(/[^+\d]/g, "")}`} className="hover:text-[#4B0F1E] transition-colors" data-testid="contact-phone-link">
                  {settings?.phone || "+91.11.41032500"}
                </a>
              </p>
              <p>
                <a href={`mailto:${settings?.email_primary || "info@hema.in"}`} className="hover:text-[#4B0F1E] transition-colors" data-testid="contact-email-primary-link">
                  {settings?.email_primary || "info@hema.in"}
                </a>
              </p>
              <p>
                <a href={`mailto:${settings?.email_secondary || "amit@hema.in"}`} className="hover:text-[#4B0F1E] transition-colors" data-testid="contact-email-secondary-link">
                  {settings?.email_secondary || "amit@hema.in"}
                </a>
              </p>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
